import type { UseGetInfiniteListParams } from 'shared/lib/api';
import {
  type CollectionSuccessApiResponse,
  getApiType,
  type MetaModel,
  useGetData,
  useGetInfiniteList,
  useGetQuery,
  useGetQueryCollection,
  usePostMutation,
  usePutMutation,
} from 'shared/lib/api';
import { pathToUrl } from 'shared/lib/router';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { logError } from 'shared/lib/log-errors';
import { mapToOptions } from 'shared/lib/mappers';
import { useDownloadFile } from 'shared/lib/hooks';
import { DEFAULT_ERROR_INFINITY_FILTERS, showDownloadStartedToast, showSuccessToast } from 'shared/lib/notifications';
import type { ContractorModel } from 'entities/contractor';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { Roles, useClientDataStore } from 'shared/lib/store';
import { useUrlResolver } from 'shared/lib/url';

import type {
  CampaignCreationWizardModel,
  CampaignNoteModel,
  CampaignPausedReason,
  CampaignSettings,
  CampaignsOutOfGroupsModel,
  CampaignsOverlappingSummaryModel,
  ContractorCampaignModel,
  ContractorGetCampaignModel,
  ContractorUpdateCampaignModel,
  CreateCampaignData,
  CreateCampaignResponse,
  GeneratedCountersModel,
  ManagerGetCampaignModel,
  ManagerUpdateCampaignModel,
  OverlappingCampaignsModel,
  OverlappingForecastModel,
  ZipCodesCountersModel,
  ZipCodesCountModel,
  ZipValidationModel,
} from '../model';
import {
  ContractorCampaignsFilter,
  ContractorCampaignsSort,
  ManagerCampaignsSort,
  ManagerOverlappingCampaignSort,
  ManagerOverlappingFilterQuery,
} from '../model';
import { campaignsKey } from '../@x';
import { useGetCampaignManagerFilters, useGetCampaignContractorFilter } from '../lib';

import { campaignRoutes } from './routes';

export const useGetCampaignsUrlFilters = () => {
  const { getRole } = useClientDataStore();
  const managerFilters = useGetCampaignManagerFilters();
  const contractorFilters = useGetCampaignContractorFilter();
  const role = getRole();
  const isContractor = role === Roles.Contractor;
  const urlFilters = isContractor ? contractorFilters : managerFilters;

  return urlFilters;
};

export function useGetCampaign(id: number, enabled = true) {
  return useGetQuery<ManagerGetCampaignModel | ContractorGetCampaignModel>(campaignRoutes[getApiType()].getCampaign, {
    urlParams: { id },
    queryKey: campaignsKey.campaign(id).queryKey,
    enabled,
  });
}

export function useGetZipCodes(id: number) {
  return useGetQuery<{ zipCodes: string[] }>(campaignRoutes[getApiType()].getZipCodes, {
    urlParams: { id },
    cacheTime: 0,
  });
}

export const useGetZipDraft = (id: number, group: 'raw' | 'normalized' | 'duplicate' | 'invalid' | 'valid') => {
  return useGetQuery<{ zipCodes: string }>(pathToUrl(campaignRoutes[getApiType()].getZipDraft, { id, group }), {
    enabled: false,
  });
};

export const useGetCampaignNotes = (campaignId: number) => {
  const ONE_MINUTE_IN_MS = 60000;

  return useGetQuery<CampaignNoteModel[]>(pathToUrl(campaignRoutes.internal.getCampaignNotes, { id: campaignId }), {
    staleTime: ONE_MINUTE_IN_MS,
  });
};

export const useGetManagerCampaignPausedReasons = (campaignId: number) => {
  return useGetQuery<CampaignPausedReason[]>(
    pathToUrl(campaignRoutes.internal.getCampaignPausedReasons, { id: campaignId })
  );
};

export const useGetContractorCampaignPausedReasons = (campaignId: number) => {
  return useGetQuery<CampaignPausedReason[]>(
    pathToUrl(campaignRoutes.app.getCampaignPausedReasons, { id: campaignId })
  );
};

const outOfGroupsKeys = createQueryKeys('campaignsOutOfGroups ', {
  campaignsOutOfGroupsList: [''],
});

export const useGetCampaignsOutOfGroups = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<CampaignsOutOfGroupsModel>(campaignRoutes.internal.getCampaignsOutOfGroups, {
    queryKey: [outOfGroupsKeys, urlFilters],
    urlFilters,
    enabled,
    keepPreviousData: true,
    refetchOnMount: false,
  });

const generatedItemsCountersKeys = createQueryKeys('overlappingGeneratedItemsCounters ', {
  generatedItemsCountersKeys: [''],
});

export const useGetGeneratedItemsCounters = ({
  campaignId,
  urlFilters,
  enabled = true,
}: {
  campaignId: string | number;
  urlFilters?: string;
  enabled?: boolean;
}) =>
  useGetQuery<GeneratedCountersModel>(pathToUrl(campaignRoutes.internal.getGeneratedItemsCounter, { id: campaignId }), {
    queryKey: [generatedItemsCountersKeys, urlFilters],
    urlFilters,
    enabled,
  });

export const useGetCampaignOverlappingSummary = (campaignId: string | number) =>
  useGetQuery<CampaignsOverlappingSummaryModel>(
    pathToUrl(campaignRoutes.internal.getCampaignOverlappingSummary, { id: campaignId })
  );

const zipCodesCountersKeys = createQueryKeys('overlappingZipCodesCounters', {
  zipCodesCountersKeys: [''],
});

export const useZipCodesCounters = ({
  campaignId,
  urlFilters,
  enabled = true,
}: {
  campaignId: string | number;
  urlFilters?: string;
  enabled?: boolean;
}) =>
  useGetQuery<ZipCodesCountersModel>(pathToUrl(campaignRoutes.internal.getZipCodesCounters, { id: campaignId }), {
    queryKey: [zipCodesCountersKeys, urlFilters],
    urlFilters,
    enabled,
  });

export function useGetCampaignsInfinityList({
  isSyncedWithUrl = false,
  filterName = '',
  getFieldValueToShow,
}: Pick<UseGetInfiniteListParams, 'isSyncedWithUrl' | 'filterName' | 'getFieldValueToShow'>) {
  return useGetInfiniteList<{ id: number; name: string }>(campaignRoutes[getApiType()].getCampaignsList, {
    defaultParams: {
      [getApiType() === 'app' ? ContractorCampaignsSort.campaignName : ManagerCampaignsSort.campaignName]: 'asc',
    },
    isSyncedWithUrl,
    filterName,
    toastOptions: {
      errorOptions: {
        errorText: DEFAULT_ERROR_INFINITY_FILTERS,
        showErrorPriority: true,
      },
    },
    getFieldValueToShow,
  });
}

export function getCampaignsOptionsFromInfinite(
  data?: InfiniteData<CollectionSuccessApiResponse<Pick<ManagerGetCampaignModel, 'id' | 'name'>>>
) {
  return mapToOptions({
    items: data?.pages.flatMap(({ data }) => data.map((item) => item)),
    textField: ({ name }) => name,
  });
}

export function useUpdateCampaignWizard(id: number) {
  return usePutMutation<CampaignCreationWizardModel>(pathToUrl(campaignRoutes[getApiType()].updateCampaign, { id }));
}

export const useCreateCampaign = () => {
  return usePostMutation<CreateCampaignData, CreateCampaignResponse>(campaignRoutes.app.createCampaign);
};

export const useSubmitCampaign = (id: string | number) => {
  return usePostMutation(pathToUrl(campaignRoutes.app.submitCampaign, { id }));
};

export const useGetCampaignSettings = (id: string | number) => {
  return useGetQuery<CampaignSettings>(pathToUrl(campaignRoutes.app.getCampaignSettings, { id }));
};

export const useSubmitZips = (id: number) => {
  return usePostMutation<{ id: number }>(pathToUrl(campaignRoutes[getApiType()].submitZips, { id }));
};

export const useCreateCampaignNote = (campaignId: number) => {
  const getCampaignNotesQuery = useGetCampaignNotes(campaignId);

  return usePostMutation<Pick<CampaignNoteModel, 'note'>, { id: number }>(
    pathToUrl(campaignRoutes.internal.createCampaignNote, { id: campaignId }),
    {
      onSuccess: () => {
        getCampaignNotesQuery.refetch().catch(logError);
      },
    }
  );
};

export const useActivateCampaignToggle = (id: number, onSuccessHandler?: () => void) => {
  const queryClient = useQueryClient();
  const urlFilters = useGetCampaignsUrlFilters();
  const getCampaignsQuery = useGetCampaigns({ enabled: false, urlFilters });
  const getCampaignQuery = useGetCampaign(id, false);

  return usePutMutation(pathToUrl(campaignRoutes.internal.deactivate, { id }), {
    onSuccess: () => {
      if (!onSuccessHandler) {
        getCampaignsQuery.refetch().catch(logError);
        queryClient.invalidateQueries(campaignsKey._def).catch(logError);
      } else {
        getCampaignQuery.refetch().catch(logError);
        onSuccessHandler();
      }
    },
  });
};

export const useRunCampaign = (id: number, onSuccessHandler?: () => void) => {
  const getCampaignQuery = useGetCampaign(id, false);
  const urlFilters = useGetCampaignsUrlFilters();
  const getCampaignsQuery = useGetCampaigns({ enabled: false, urlFilters });

  return usePutMutation(pathToUrl(campaignRoutes.internal.runForce, { id }), {
    onSuccess: () => {
      getCampaignQuery.refetch().catch(logError);
      getCampaignsQuery.refetch().catch(logError);
      onSuccessHandler?.();
    },
  });
};

export const useApproveCampaign = (id: number) => {
  const queryClient = useQueryClient();
  const urlFilters = useGetCampaignsUrlFilters();
  const getCampaignsQuery = useGetCampaigns({ enabled: false, urlFilters });

  return usePutMutation(pathToUrl(campaignRoutes.internal.approveCampaign, { id }), {
    onSuccess: () => {
      getCampaignsQuery.refetch().catch(logError);
      queryClient.invalidateQueries(campaignsKey._def).catch(logError);
    },
  });
};

export function useUpdateCampaign(id: number, skipCampaignUpdate = false, skipCampaignRefetch = false) {
  const queryClient = useQueryClient();
  const urlFilters = useGetCampaignsUrlFilters();
  const getCampaignsQuery = useGetCampaigns({ enabled: false, urlFilters });
  const enabledZipCodesRefetch = getApiType() === 'internal';
  const getCampaignCountersQuery = useZipCodesCounters({ campaignId: id, enabled: enabledZipCodesRefetch });

  return usePutMutation<ManagerUpdateCampaignModel | ContractorUpdateCampaignModel>(
    pathToUrl(campaignRoutes[getApiType()].updateCampaign, { id }),
    {
      onSuccess: () => {
        if (!skipCampaignRefetch) {
          getCampaignsQuery.refetch().catch(logError);
        }
        if (!skipCampaignUpdate) {
          queryClient.invalidateQueries(campaignsKey.campaign(id).queryKey).catch(logError);
        }
        if (enabledZipCodesRefetch) {
          getCampaignCountersQuery.refetch().catch(logError);
        }
      },
    }
  );
}

export function useValidateZipCodes(id: number) {
  return usePutMutation<{ zipCodes: string }, { zipCodes: ZipValidationModel }>(
    pathToUrl(campaignRoutes[getApiType()].updateCampaign, { id })
  );
}

const overlappingKeys = createQueryKeys('managerOverlapping', {
  overlappingKeys: [''],
});

export function useGetCampaigns<T>({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) {
  return useGetQueryCollection<T>(campaignRoutes[getApiType()].getCampaigns, {
    queryKey: [overlappingKeys.overlappingKeys, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });
}

export function useGetInfinityCampaigns<T>({ isSyncedWithUrl = false, filterName = '' } = {}) {
  return useGetInfiniteList<T>(campaignRoutes[getApiType()].getCampaignsList, {
    isSyncedWithUrl,
    filterName,
    defaultParams: { [ManagerOverlappingCampaignSort.name]: 'asc' },
    getFieldValueToShow: (item) => item.name,
  });
}

export function getSingleOverbudgetCampaign() {
  return useGetData<{ data: ContractorCampaignModel; meta: MetaModel }>({
    url: campaignRoutes[getApiType()].getCampaigns,
    queryParams: { [ContractorCampaignsFilter.percent80]: '80', limit: '1' },
  });
}

export function useGetCampaignZipCodesCount(id: number, minimalIncome: number) {
  return useGetQuery<ZipCodesCountModel>(campaignRoutes.internal.getZipCodesCounts, {
    urlParams: { id },
    queryParams: { minimalIncome },
    cacheTime: 0,
  });
}

export const useGetZipCodesCSV = (id: number) =>
  useDownloadFile({ url: pathToUrl(campaignRoutes.internal.getZipCodesCSV, { id }) });

const DOWNLOAD_TOAST = 'DownloadUniqueCampaignZipCSV';
const SUCCESS_TOAST = 'SuccessUniqueCampaignZipCSV';

export const useDownloadUniqueCampaignZipCSV = (id: number) => {
  const { download } = useDownloadFile({
    url: pathToUrl(campaignRoutes.internal.getUniqueCampaignZips, { id }),
    type: 'text',
  });

  const handleDownload = () => {
    showDownloadStartedToast('This download will take a moment, please wait...', DOWNLOAD_TOAST);

    return download().finally(() => {
      showSuccessToast('The download is successfully completed', SUCCESS_TOAST);
    });
  };

  return { download: handleDownload };
};

export const useGetOutOfGroupCampaignZIP = () => {
  return useDownloadFile({ url: campaignRoutes.internal.getOutOfGroupCampaignZIP, type: 'zip' });
};

export const useGetOverlappingCampaigns = ({
  campaignId,
  urlFilters,
  enabled,
}: {
  campaignId: string | number;
  urlFilters?: string;
  enabled?: boolean;
}) =>
  useGetQueryCollection<OverlappingCampaignsModel>(campaignRoutes.internal.getOverlappingCampaigns, {
    queryKey: [urlFilters],
    urlParams: { id: campaignId },
    urlFilters,
    enabled,
    refetchOnMount: false,
    keepPreviousData: false,
  });

export const useGetOverlappingForecast = ({ campaignId, enabled }: { campaignId: string | number; enabled: boolean }) =>
  useGetQuery<OverlappingForecastModel>(campaignRoutes.internal.getOverlappingForecast, {
    urlParams: { id: campaignId },
    enabled,
    cacheTime: 0,
  });

export const useCreateEmailIntegration = (campaignId: number) => {
  const queryClient = useQueryClient();

  return usePostMutation<{
    emails: string[];
  }>(campaignRoutes.internal.campaignEmailIntegrations, {
    onSuccess: () => {
      void queryClient.invalidateQueries(campaignsKey.campaign(campaignId).queryKey);
    },
  });
};

export const useUpdateEmailIntegration = (campaignId: number) => {
  const queryClient = useQueryClient();

  return usePutMutation<{
    emails: string[];
  }>(campaignRoutes.internal.campaignEmailIntegrations, {
    onSuccess: () => {
      void queryClient.invalidateQueries(campaignsKey.campaign(campaignId).queryKey);
    },
  });
};

export const useDownloadOverlappingZIP = () => {
  const { getParam } = useUrlResolver();
  const campaignType = getParam(ManagerOverlappingFilterQuery.type) ?? '';
  const campaignTypeFilter = campaignType ? `?${ManagerOverlappingFilterQuery.type}=${campaignType}` : '';

  return useDownloadFile({
    url: campaignRoutes.internal.getOverlappingZIP,
    urlFilters: campaignTypeFilter,
    type: 'zip',
  });
};

export function useGetContractorsOutOfGroupInfinityList({ isSyncedWithUrl = false, filterName = '' } = {}) {
  return useGetInfiniteList<Pick<ContractorModel, 'id' | 'companyName'>>(
    campaignRoutes.internal.getContractorsOutOfGroups,
    {
      defaultParams: { [ManagerOverlappingCampaignSort.companyName]: 'asc' },
      isSyncedWithUrl,
      filterName,
      toastOptions: {
        errorOptions: {
          errorText: DEFAULT_ERROR_INFINITY_FILTERS,
          showErrorPriority: true,
        },
      },
    }
  );
}

export const useGetManagerTestLeadCrmHistoryJSON = (id: number, historyId: number) =>
  useDownloadFile({
    url: pathToUrl(campaignRoutes.internal.getTestLeadCrmHistoryJSON, { id, historyId }),
    type: 'json',
  });
